import { useCallback } from 'react'

import { ApiError, PasswordResetRestControllerService } from '@api'
import { zodResolver } from '@hookform/resolvers/zod'
import ReCaptcha from 'react-google-recaptcha'
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form'
import { useQuery } from 'react-query'
import { useNavigate } from 'react-router'
import * as z from 'zod'

import { Button } from '@/components/ui/button.tsx'
import { FormField } from '@/components/ui/form/form-field.tsx'
import { useToast } from '@/components/ui/toast.tsx'
import { AppRoutes, CAPTCHA_SITE_KEY } from '@/utils/constants'
import { handleError } from '@/utils/error'

import { useAccount } from '../hooks/useAccounts'

export type ResetPasswordFormValues = z.infer<typeof resetPasswordFormSchema>

const resetPasswordFormSchema = z
  .object({
    password: z
      .string()
      .min(8, { message: 'Password must be at least 8 characters' }),
    confirmPassword: z
      .string()
      .min(1, { message: 'Confirm Password is required' }),
    'g-recaptcha-response': z.string().nonempty(),
    isTfaRequired: z.boolean(),
    tfaCode: z.string().optional(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    path: ['confirmPassword'],
    message: "Passwords don't match",
  })
  .refine((data) => !data.isTfaRequired || !!data.tfaCode, {
    path: ['tfaCode'],
    message: 'TFA code is required',
  })

const INVALID_TFA_ERROR = 'Code is not correct'

interface Props {
  token: string
}

export const ResetPassword = ({ token }: Props) => {
  const form = useForm<ResetPasswordFormValues>({
    mode: 'onChange',
    resolver: zodResolver(resetPasswordFormSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
      'g-recaptcha-response': '',
      isTfaRequired: false,
      tfaCode: '',
    },
  })

  useQuery(
    'isTfaRequired',
    () => PasswordResetRestControllerService.checkPasswordResetToken({ token }),
    {
      onSuccess: ({ tfaEnabled }) => {
        form.setValue('isTfaRequired', !!tfaEnabled)
      },
      cacheTime: 0,
      refetchInterval: 60000,
    },
  )

  const isTfaRequired = useWatch({
    control: form.control,
    name: 'isTfaRequired',
  })

  const { handleSubmit } = form
  const { resetPassword } = useAccount()
  const navigate = useNavigate()
  const toast = useToast()

  const onSubmit = useCallback(
    async (values: ResetPasswordFormValues) => {
      try {
        await resetPassword.mutateAsync({
          'g-recaptcha-response': values['g-recaptcha-response'],
          token,
          newPassword: values.confirmPassword,
          tfaCode: values.tfaCode,
        })
        toast.success(
          'Your password has successfully been reset! You can now login 🎉',
        )
        navigate(AppRoutes.Auth.Login.asUrl())
      } catch (error) {
        if (!(error instanceof ApiError)) return

        if (error.body?.status === 429) {
          toast.error(
            'You already requested a password reset too often. Contact the support to get help resetting your password.',
          )

          return
        }
        if (error.body?.detail === INVALID_TFA_ERROR) {
          toast.error('TFA code is not correct. Please try again')

          return
        }

        handleError(
          error,
          'Oops, there was an error. Maybe your captcha was wrong?',
        )
      }
    },
    [resetPassword, token, navigate, toast],
  )

  return (
    <div>
      <FormProvider {...form}>
        <form
          className="grid gap-8"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="grid gap-4">
            <FormField
              name="password"
              label="Password"
              placeholder="Password (at least 8 character)"
              type="password"
              isPassword
            />

            <FormField
              name="confirmPassword"
              label="Confirm Password"
              placeholder="Password (at least 8 character)"
              type="password"
              isPassword
            />

            {isTfaRequired && (
              <FormField
                name="tfaCode"
                label="Two-factor Authentication Code"
                placeholder="TFA Code"
              />
            )}

            <Controller
              control={form.control}
              name="g-recaptcha-response"
              render={({ field: { onChange } }) => (
                <ReCaptcha
                  sitekey={CAPTCHA_SITE_KEY}
                  onChange={(value) => {
                    if (!value) {
                      return
                    }

                    onChange(value)
                  }}
                />
              )}
            />
          </div>

          <Button
            disabled={!form.formState.isDirty || !form.formState.isValid}
            className="w-full"
          >
            Change Password
          </Button>
        </form>
      </FormProvider>
    </div>
  )
}
